import { useEffect, useState } from 'react';
import { Loader2, Search, Plus, Pencil, Trash2, X, RefreshCw } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { formatDate } from '@/lib/dateFormat';
import { toast } from '@/components/ui/toast';
import { DateInput } from '@/components/ui/DateInput';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { SelectField } from '@/components/ui/SelectField';

// Same layout as the vendor Campaigns page. "My Campaigns (SaaS)" are the
// platform's own campaigns (editable); picking an organization shows that
// vendor's campaigns read-only.

const STATUS_COLORS: Record<string, string> = {
  Active: 'bg-emerald-50 text-emerald-700',
  Paused: 'bg-amber-50 text-amber-700',
  Completed: 'bg-slate-100 text-slate-500',
};
const PROVIDERS = ['Facebook', 'Google', 'LinkedIn', 'Instagram', 'WhatsApp', 'Email', 'Other'];
const STATUSES = ['Active', 'Paused', 'Completed'];

export function SuperAdminCampaignsPage() {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('platform'); // 'platform' or tenant ID
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState<{ open: boolean; campaign?: any }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadCampaigns = () => {
    setLoading(true);
    const req = view === 'platform'
      ? saApi.get('/super-admin/campaigns')
      : saApi.get('/super-admin/campaigns/vendor', { params: { tenantId: view } });
    req.then(({ data }) => setCampaigns(Array.isArray(data) ? data : []))
      .catch((e: any) => toast('error', e?.response?.data?.message || 'Failed to load campaigns'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    saApi.get('/super-admin/organizations')
      .then(({ data }) => setTenants((Array.isArray(data) ? data : []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => {});
  }, []);
  useEffect(() => { loadCampaigns(); }, [view]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? campaigns.filter(c => String(c.name).toLowerCase().includes(q) || String(c.provider).toLowerCase().includes(q))
    : campaigns;
  const isPlatform = view === 'platform';
  const { sorted, sort, onSort } = useSort(filtered);

  const toggleActive = async (c: any) => {
    setTogglingId(c.id);
    try {
      await saApi.patch(`/super-admin/campaigns/${c.id}`, { isActive: !c.isActive });
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, isActive: !x.isActive } : x));
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await saApi.delete(`/super-admin/campaigns/${confirmDelete.id}`);
      toast('success', 'Campaign deleted');
      setConfirmDelete(null);
      loadCampaigns();
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const colCount = isPlatform ? 6 : 5;

  return (
    <SuperAdminLayout title="Campaigns">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Campaigns</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {isPlatform ? 'Manage BrixLead marketing campaigns' : `Campaigns of ${tenants.find(t => t.id === view)?.name ?? 'this organization'} (read-only)`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadCampaigns} title="Refresh"
              className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 text-slate-500 hover:text-[#6366F1] hover:border-[#6366F1] transition-colors">
              <RefreshCw className="h-4 w-4" />
            </button>
            {isPlatform && (
              <button onClick={() => setDrawer({ open: true })}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors">
                <Plus className="h-4 w-4" /> Add Campaign
              </button>
            )}
          </div>
        </div>

        {/* Search + organization */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search campaigns..."
              className="w-full h-10 pl-9 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-[#6366F1]" />
          </div>
          <SelectField value={view} onChange={e => setView(e.target.value)}
            className="h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]">
            <option value="platform">My Campaigns (SaaS)</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SelectField>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50">
            <span className="text-xs text-slate-400">{loading ? 'Loading...' : `${filtered.length} campaign${filtered.length !== 1 ? 's' : ''}`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh label="S.No" sortable={false} />
                  <SortTh label="Campaign Name" sortKey="name" sort={sort} onSort={onSort} />
                  <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortTh label="Active" sortKey="isActive" sort={sort} onSort={onSort} />
                  <SortTh label="Dates" sortKey="startDate" sort={sort} onSort={onSort} />
                  {isPlatform && <SortTh label="Actions" sortable={false} />}
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr><td colSpan={colCount} className="px-4 py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1] mx-auto" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={colCount} className="px-4 py-16 text-center text-slate-400 text-sm">
                    {q ? 'No campaigns match your search.' : isPlatform ? "No campaigns yet. Click '+ Add Campaign' to create one." : 'No campaigns for this organization.'}
                  </td></tr>
                ) : sorted.map((c: any, idx: number) => (
                  <tr key={c.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3">
                      {isPlatform ? (
                        <button onClick={() => setDrawer({ open: true, campaign: c })}
                          className="font-medium text-[#6366F1] hover:underline text-left">
                          {c.name}
                        </button>
                      ) : (
                        <p className="font-medium text-slate-800">{c.name}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-0.5">{c.provider}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {togglingId === c.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      ) : (
                        <button
                          onClick={() => isPlatform && toggleActive(c)}
                          disabled={!isPlatform}
                          title={isPlatform ? (c.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate') : 'Read-only (vendor campaign)'}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${c.isActive ? 'bg-[#6366F1]' : 'bg-gray-200'}`}>
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${c.isActive ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      <div className="text-xs">
                        {c.startDate ? formatDate(c.startDate) : '—'} <span className="text-slate-300 mx-1">to</span> {c.endDate ? formatDate(c.endDate) : '—'}
                      </div>
                    </td>
                    {isPlatform && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => setDrawer({ open: true, campaign: c })} title="Edit" className="p-1.5 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setConfirmDelete(c)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drawer.open && (
        <CampaignDrawer campaign={drawer.campaign}
          onClose={() => setDrawer({ open: false })}
          onSaved={() => { setDrawer({ open: false }); loadCampaigns(); }} />
      )}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete Campaign?"
        message={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        isLoading={deleting}
      />
    </SuperAdminLayout>
  );
}

// ── Campaign Drawer (same look as the vendor one) ─────────────────────────────
function CampaignDrawer({ campaign, onClose, onSaved }: { campaign?: any; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!campaign;
  const [form, setForm] = useState({
    name: campaign?.name ?? '',
    provider: campaign?.provider ?? 'Facebook',
    status: campaign?.status ?? 'Active',
    isActive: campaign?.isActive ?? true,
    startDate: campaign?.startDate?.slice(0, 10) ?? '',
    endDate: campaign?.endDate?.slice(0, 10) ?? '',
    budget: campaign?.budget != null ? String(campaign.budget) : '',
    description: campaign?.description ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast('error', 'Campaign name is required'); return; }
    // Platform campaigns require both dates (the backend stores them as required).
    if (!form.startDate || !form.endDate) { toast('error', 'Start and end dates are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, budget: form.budget ? parseFloat(form.budget) : null, description: form.description || null, tenantId: null };
      if (isEdit) await saApi.patch(`/super-admin/campaigns/${campaign.id}`, payload);
      else await saApi.post('/super-admin/campaigns', payload);
      toast('success', isEdit ? 'Campaign updated' : 'Campaign created');
      onSaved();
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to save campaign');
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-slate-400';
  const selectCls = `${inputCls} bg-white`;
  const labelCls = 'block text-xs font-normal text-slate-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit Campaign' : 'New Campaign'}</h2>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Campaign Name <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="Enter campaign name" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Provider</label>
            <SelectField className={selectCls} value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </SelectField>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <SelectField className={selectCls} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </SelectField>
          </div>
          <div>
            <label className={labelCls}>Campaign Duration <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="block text-[11px] text-slate-400 mb-1">Start Date</span>
                <DateInput value={form.startDate} onChange={v => setForm(f => ({ ...f, startDate: v }))} placeholder="Start date" className="h-10" />
              </div>
              <div>
                <span className="block text-[11px] text-slate-400 mb-1">End Date</span>
                <DateInput value={form.endDate} onChange={v => setForm(f => ({ ...f, endDate: v }))} placeholder="End date" className="h-10" />
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Budget (optional)</label>
            <input type="number" className={inputCls} placeholder="e.g. 5000" value={form.budget}
              onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Description (optional)</label>
            <textarea rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
              placeholder="Campaign description" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex items-center justify-between">
            <label className={labelCls + ' mb-0'}>Active</label>
            <button type="button" onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-[#6366F1]' : 'bg-gray-200'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-gray-200 hover:border-gray-300">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}
